import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { ContactForm } from "@/components/marketing/contact/ContactForm";
import { GoogleReviewBanner } from "@/components/marketing/contact/GoogleReviewBanner";
import { systemLabel } from "@/components/marketing/visuals/flow/flowConfigs";
import { enquiryMailto } from "@/lib/enquiry";
import { isKnownContactSource } from "@/components/marketing/solution/constants";
import { ENQUIRY_EMAIL } from "@/lib/brand";

type ContactSearch = {
  systems?: string;
  /** Which page sent them here. Allowlisted - see `isKnownContactSource`. */
  source?: string;
};

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  validateSearch: (search: Record<string, unknown>): ContactSearch => ({
    systems: typeof search.systems === "string" ? search.systems : undefined,
    // Unrecognised values are dropped rather than passed through: this lands in
    // the CRM, so an unvalidated parameter would let anyone write arbitrary
    // text into lead records by sharing a crafted link.
    source: isKnownContactSource(search.source) ? search.source : undefined,
  }),
});

function humanJoin(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function buildInitialMessage(systems?: string): string | undefined {
  if (!systems) return undefined;
  const labels = systems
    .split(",")
    .map((id) => systemLabel(id.trim()))
    .filter((label): label is string => Boolean(label));
  if (labels.length === 0) return undefined;
  return `Hi - we use ${humanJoin(labels)} and would like to connect them.`;
}

function ContactPage() {
  const { systems, source } = Route.useSearch();
  const initialMessage = buildInitialMessage(systems);

  return (
    <MarketingLayout>
      <Helmet>
        <title>Contact Us | Larasoft</title>
        <meta
          name="description"
          content="Get in touch with Larasoft. Have a question about our data sync solutions? We'd love to hear from you."
        />
        <link rel="canonical" href="https://larasoft.global/contact" />
        <meta property="og:title" content="Contact Larasoft" />
        <meta
          property="og:description"
          content="Get in touch with our team about data sync solutions."
        />
        <meta property="og:url" content="https://larasoft.global/contact" />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-2xl space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-brand-blue-dark">
              Contact Us
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Have a question? We'd love to hear from you.
            </p>
          </div>
          <ContactForm
            source={source ?? "marketing:contact"}
            initialMessage={initialMessage}
          />
          <div className="text-center text-sm text-slate-500">
            <p>
              Or email us directly at{" "}
              <a
                href={enquiryMailto("/contact")}
                className="text-brand-blue-medium hover:underline"
              >
                {ENQUIRY_EMAIL}
              </a>
            </p>
          </div>
          <GoogleReviewBanner />
        </div>
      </Section>
    </MarketingLayout>
  );
}
